import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import RcTree from 'rc-tree';
import { SVG } from 'components/svg';
import { TreeWrapper } from './tree.styled';
import { Icons } from 'assets';

import type {
  TreeHandle,
  TreeNode,
  TreeNodeKey,
  TreeProps,
} from './tree.types';

function collectParentKeys(
  nodes: TreeNode[],
  result = new Set<TreeNodeKey>()
): Set<TreeNodeKey> {
  for (const node of nodes) {
    if (node.children?.length) {
      result.add(node.key);
      collectParentKeys(node.children, result);
    }
  }

  return result;
}

export const Tree = forwardRef<TreeHandle, TreeProps>((props, ref) => {
  const {
    treeData,
    checkedKeys,
    onCheck,
    defaultExpandAll,
    defaultExpandedKeys,
    checkable = true,
    height,
    className,
    style,
  } = props;

  const [expandedKeys, setExpandedKeys] = useState<TreeNodeKey[] | undefined>(
    undefined
  );

  const parentKeys = useMemo(() => collectParentKeys(treeData), [treeData]);

  useImperativeHandle(ref, () => ({
    expandAll: () => setExpandedKeys(Array.from(parentKeys)),
    collapseAll: () => setExpandedKeys([]),
  }));

  const handleCheck = (
    keys: TreeNodeKey[] | { checked: TreeNodeKey[]; halfChecked: TreeNodeKey[] }
  ) => {
    if (!onCheck) {
      return;
    }

    const checked = Array.isArray(keys) ? keys : keys.checked;
    const halfChecked = Array.isArray(keys) ? [] : keys.halfChecked;

    onCheck(
      checked.filter((k) => !parentKeys.has(k)),
      halfChecked
    );
  };

  const handleSelect = (
    _keys: TreeNodeKey[],
    info: { node: { key: TreeNodeKey; children?: unknown[] } }
  ) => {
    const isLeaf = !info.node.children?.length;

    if (!isLeaf || !onCheck) {
      return;
    }

    const key = info.node.key;
    const current = checkedKeys ?? [];
    const isChecked = current.includes(key);
    const next = isChecked
      ? current.filter((k) => k !== key)
      : [...current, key];
    onCheck(next, []);
  };

  return (
    <TreeWrapper className={className} style={style}>
      <RcTree
        treeData={treeData}
        checkedKeys={checkedKeys}
        onCheck={handleCheck}
        onSelect={handleSelect}
        defaultExpandAll={defaultExpandAll}
        defaultExpandedKeys={defaultExpandedKeys}
        {...(expandedKeys !== undefined && {
          expandedKeys,
          onExpand: (keys) => setExpandedKeys(keys as TreeNodeKey[]),
        })}
        checkable={checkable}
        switcherIcon={(props) => (
          <SVG
            src={Icons.Chevron}
            props={{
              deg: props.expanded ? 0 : -90,
            }}
          />
        )}
        selectable
        prefixCls="rc-tree"
        expandAction="click"
        virtual
        height={height}
        itemHeight={32}
      />
    </TreeWrapper>
  );
});
